import { PositionalAudio, useTexture } from '@react-three/drei';
import { type ThreeElements } from '@react-three/fiber';
import { useControls } from 'leva';
import { useLayoutEffect, useMemo, useRef } from 'react';
import {
  color,
  dot,
  Fn,
  hash,
  instanceIndex,
  modelNormalMatrix,
  normalGeometry,
  normalize,
  positionLocal,
  smoothstep,
  texture,
  uniform,
  uv,
  vec3,
  vec4,
  vertexStage,
  type ShaderNodeObject,
} from 'three/tsl';
import * as THREE from 'three';
import { threeColorGradient } from './shaders/lights';
import { type Node } from 'three/webgpu';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { billboardPositionNode } from '../utils/tsl-nodes';
import { windNoiseFunction } from '../utils/tsl-noise';
import type { IslandType } from '../Experience';
import { InstancedStaticCollider } from 'bvhecctrl';
import useGameStore from '../store/useGameStore';

type Bushes = {
  nodes: IslandType['nodes'];
  forest_mat: THREE.MeshStandardMaterial;
};
export const Brush = ({ nodes, forest_mat }: Bushes) => {
  const leavesRef = useRef<ThreeElements['spriteNodeMaterial']>(null!);
  const spriteMeshRef = useRef<THREE.Sprite>(null!);
  const sphereRef = useRef<ThreeElements['meshBasicNodeMaterial']>(null!);
  const [leavesTexture] = useTexture(['/texture/leaves.png']);

  const leavesInstanceMeshRef = useRef<THREE.InstancedMesh>(null!);
  const treeInstanceMeshRef = useRef<THREE.InstancedMesh>(null!);
  const controls = useControls(
    'leaves',
    {
      colorFinal: {
        value: '#306E5AFF',
      },
      colorMid: {
        value: '#539E43FF',
      },
      colorStart: {
        value: '#9FCB68FF',
      },
      lightPos: [0, 0, 3],
      pointlightPos: [0, 2.5, 0],
      pLightX: {
        value: -1,
        min: -10,
        max: 10,
      },
      pLightY: {
        value: 1,
        min: -10,
        max: 10,
      },
      pLightZ: {
        value: 0,
        min: -10,
        max: 10,
      },
      decay: {
        value: 0.25,
        min: 0,
        max: 0.5,
      },
      low: {
        value: -0.8,
        min: -4,
        max: 4,
        step: 0.001,
      },
      high: {
        value: 1.5,
        min: -4,
        max: 4,
        step: 0.001,
      },
    },
    {
      collapsed: true,
    }
  );

  /** -------------
      State management
     ----------------*/
  const playAudio = useGameStore((state) => state.playAudio);

  const [perlinNoiseTexture2] = useTexture(['/texture/noise/perlin2.png']);
  perlinNoiseTexture2.wrapS = perlinNoiseTexture2.wrapT = THREE.RepeatWrapping;

  const mergedGeometry = useMemo(() => {
    const planes = [];
    const count = 80;
    for (let i = 0; i < count; i++) {
      const plane = new THREE.PlaneGeometry(1, 1);

      //position
      const spherical = new THREE.Spherical(
        1 - Math.pow(Math.random(), 3),
        Math.PI * 2 * Math.random(),
        Math.PI * Math.random()
      );

      //position
      const position = new THREE.Vector3().setFromSpherical(spherical);
      //   plane.rotateX(Math.random() * 9999);
      //   plane.rotateY(Math.random() * 9999);
      //   plane.rotateZ(Math.random() * 9999);
      plane.translate(position.x, position.y, position.z);

      //Normal
      const normal = position.clone().normalize();
      const noramlArray = new Float32Array(12);
      for (let i = 0; i < 4; i++) {
        const i3 = i * 3;
        const position = new THREE.Vector3(
          plane.attributes.position.array[i3],
          plane.attributes.position.array[i3 + 1],
          plane.attributes.position.array[i3 + 2]
        );

        const mixedNormal = position.lerp(normal, 0.4);
        noramlArray[i3] = mixedNormal.x;
        noramlArray[i3 + 1] = mixedNormal.y;
        noramlArray[i3 + 2] = mixedNormal.z;
      }

      plane.setAttribute('normal', new THREE.BufferAttribute(noramlArray, 3));
      planes.push(plane);
    }
    return mergeGeometries(planes);
  }, []);

  //   const brushCount = useMemo(() => 10, []);

  //   useLayoutEffect(() => {
  //     if (!spriteMeshRef.current.geometry) return;
  //     // const sphereGeo = new THREE.SphereGeometry(2);
  //     // const sphereNormals = sphereGeo.attributes.normal;
  //     // spriteMeshRef.current.geometry.setAttribute('normal', sphereNormals);
  //     // spriteMeshRef.current.geometry.attributes.normal.needsUpdate = true;

  //     if (!spriteMeshRef.current.geometry.attributes.normal) {
  //       spriteMeshRef.current.geometry.computeVertexNormals();
  //       applySphericalNormals(spriteMeshRef.current.geometry);
  //     }
  //   }, []);

  const { shaderNode } = useMemo(() => {
    const brushTexture = texture(leavesTexture, uv());
    const rChannel = brushTexture.r.greaterThan(0); //greater than removes banding

    /**----------------
          uniforms node
    -----------------*/
    const mainColor = uniform(color(controls.colorFinal)).toVar();
    const uColorMid = uniform(color(controls.colorMid)).toVar();
    const uColorStart = uniform(color(controls.colorStart)).toVar();
    const pointLightDirection = uniform(
      vec3(controls.pLightX, controls.pLightY, controls.pLightZ),
      'vec3'
    );

    const lightShading = Fn(
      ([direction, low, high, normal]: [
        ShaderNodeObject<Node>,
        ShaderNodeObject<Node>,
        ShaderNodeObject<Node>,
        ShaderNodeObject<Node>
      ]) => {
        const nDirection = normalize(direction);
        const intensity = dot(normal, nDirection);
        intensity.assign(smoothstep(low, high, intensity));

        return intensity;
      }
    );

    //vertex calculations
    //compute model normal at the vertex stage
    const vNormal = vertexStage(
      modelNormalMatrix.mul(vec4(normalGeometry, 0.0))
    );
    const normal = normalize(vNormal);
    /**----------------
        position node
    -----------------*/
    const wind = windNoiseFunction(perlinNoiseTexture2, positionLocal);
    const randomness = hash(instanceIndex).add(0.5);

    const posNode = Fn(() => {
      const windIntensity = 4;

      return billboardPositionNode(positionLocal).add(
        vec3(
          wind.x.mul(randomness).mul(windIntensity),
          0,
          wind.y.mul(randomness).mul(windIntensity)
        )
      );
    })();

    /**----------------
        Color node
    -----------------*/
    const colorNode = Fn(() => {
      const light = vec3(0.0);

      const shading = lightShading(pointLightDirection, -0.8, 1.5, normal);

      light.addAssign(shading);

      mainColor.mulAssign(light);
      const finalColor = threeColorGradient(
        shading,
        color(controls.colorFinal), //lvl 1
        color('#357D44'), //lvl 2
        uColorMid, //lvl 3
        uColorStart //lvl 4
      );

      mainColor.assign(finalColor);

      return vec4(mainColor, rChannel);
    })();

    if (leavesRef.current) {
      leavesRef.current.needsUpdate = true;
    }
    if (spriteMeshRef.current) {
      sphereRef.current.needsUpdate = true;
    }

    return {
      shaderNode: {
        colorNode,
        posNode,
      },
    };
  }, [leavesTexture, controls, perlinNoiseTexture2]);

  const branchPositions = Object.entries(nodes)
    .filter(([key]) => key.startsWith('ref_'))
    .map(([, value]) => value.matrix);

  const treePositions = Object.entries(nodes)
    .filter(([key]) => key.startsWith('tree'))
    .map(([, value]) => value.matrix);
  useLayoutEffect(() => {
    for (let i = 0; i < branchPositions.length; i++) {
      leavesInstanceMeshRef.current.setMatrixAt(i, branchPositions[i]);
    }

    for (let i = 0; i < treePositions.length; i++) {
      treeInstanceMeshRef.current.setMatrixAt(i, treePositions[i]);
    }
  }, [branchPositions, treePositions]);
  return (
    <>
      <group scale={1}>
        {/* branch leaves */}
        <instancedMesh
          ref={leavesInstanceMeshRef}
          args={[undefined, undefined, branchPositions.length]}
          geometry={mergedGeometry}
          castShadow
          receiveShadow
        >
          <meshBasicNodeMaterial
            ref={leavesRef}
            colorNode={shaderNode.colorNode}
            positionNode={shaderNode.posNode}
            transparent={true}
          />
        </instancedMesh>

        {/* trees */}
        <InstancedStaticCollider>
          <instancedMesh
            args={[undefined, undefined, treePositions.length]}
            ref={treeInstanceMeshRef}
            geometry={nodes.tree_bare_fat.geometry}
            material={forest_mat}
            castShadow
            receiveShadow
          >
            {/* <meshBasicNodeMaterial map={forest_mat.map} /> */}
          </instancedMesh>
        </InstancedStaticCollider>
        {playAudio && (
          <PositionalAudio autoplay loop url='/audio/birds.mp3' distance={3} />
        )}
      </group>
      {/* light position */}
      <mesh position={[controls.pLightX, controls.pLightY, controls.pLightZ]}>
        <icosahedronGeometry args={[0.1, 2]} />
        <meshBasicNodeMaterial color={[1, 0.1, 0.1]} />
      </mesh>
    </>
  );
};
